/**
 * Text formatting for descriptions and notes. Each text has a format:
 *   text      plain text (line breaks and spaces kept), only links, #123, ~456 and @mentions
 *   markdown  **bold**  *italic*  `code`  ```code block```  # heading (1-3)  > quote  - list  1. list  ---
 *             [text](url)  ![alt](image url)
 *   bbcode    the tags of Mantis' BBCodePlus plugin: [b] [i] [u] [s] [sub] [sup] [small] [color=…] [highlight=…]
 *             [size=…] [left] [center] [right] [justify] [h1]…[h6] [url] [url=…] [email] [img] [code] [code=lang]
 *             [quote] [quote=name] [list] [list=1] [list=a] [*] [hr] [table] [tr] [th] [td] [youtube]
 *   auto      both syntaxes (imported Mantis texts often mix them); detectFormat() tells which one a text uses
 * Plain Mantis texts stay as they were: `_` is not italic, indented lines are not code, unknown or unclosed tags
 * (e.g. [Setup] in test logs) stay text. Rendered as React elements: no HTML from the text is ever used.
 */
import { Fragment, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

export type TextFormat = 'auto' | 'text' | 'markdown' | 'bbcode';
interface Syntax {
  md: boolean;
  bb: boolean;
}
const SYNTAX: Record<TextFormat, Syntax> = {
  auto: { md: true, bb: true },
  text: { md: false, bb: false },
  markdown: { md: true, bb: false },
  bbcode: { md: false, bb: true },
};

const safeUrl = (url: string, image = false): string | null => {
  const u = url.trim();
  if (/^https?:\/\//i.test(u)) return u;
  if (!image && /^(mailto:|ftp:\/\/)/i.test(u)) return u;
  if (/^\/(?!\/)/.test(u)) return u; // same site, e.g. /api/attachments/12?inline=1
  if (!image && u.startsWith('#')) return u;
  return null;
};

const safeColor = (c: string): string | null => (/^#[0-9a-f]{3,8}$/i.test(c) || /^[a-z]{3,20}$/i.test(c) ? c : null);

const external = (href: string) => (/^(https?|ftp):/i.test(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {});

// Markdown inline tokens (groups 1-9), then links and references (groups 10-16)
const INLINE =
  /(`+)([^`\n]+?)\1|!\[([^\]\n]*)\]\(([^)\s]+)\)|\[([^\]\n]+)\]\(([^)\s]+)\)|\*\*(?=\S)([^\n]*?\S)\*\*|(^|[^\w*])\*(?=[^\s*])([^*\n]*?[^\s*])\*(?![\w*])|(https?:\/\/[^\s<>"')\]]+)|(^|[^\w&/])#(\d+)\b|(^|[^\w/])~(\d+)\b|(^|[\s(\[{"'>,;:!?])@([\w.+-]*\w)/g;

// BBCode inline tags with their closing tag
const BB_TAGS = 'b|i|u|s|sub|sup|small|center|left|right|justify|color|highlight|size|url|email|img|code|youtube|h[1-6]';
const BB_INLINE = new RegExp(`\\[(${BB_TAGS})(?:=([^\\]\\n]*))?\\]([\\s\\S]*?)\\[\\/\\1\\]`, 'gi');

/** BBCode tags first (Mantis texts), the text between them as Markdown (when those syntaxes are on). */
function inline(text: string, keyBase: string, syn: Syntax, noLinks = false): ReactNode[] {
  if (!syn.bb) return markdownInline(text, keyBase, syn, noLinks);
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  for (const m of text.matchAll(BB_INLINE)) {
    out.push(...markdownInline(text.slice(last, m.index), `${keyBase}.${k++}`, syn, noLinks));
    out.push(bbTag(m, `${keyBase}.${k++}`, syn, noLinks));
    last = m.index! + m[0].length;
  }
  out.push(...markdownInline(text.slice(last), `${keyBase}.${k++}`, syn, noLinks));
  return out.filter((n) => n !== '');
}

function bbTag(m: RegExpMatchArray, key: string, syn: Syntax, noLinks: boolean): ReactNode {
  const tag = m[1].toLowerCase();
  const arg = (m[2] ?? '').trim();
  const body = m[3];
  const kids = (plain = noLinks) => inline(body, key, syn, plain);
  const span = (style: CSSProperties, className?: string) => (
    <span key={key} style={style} className={className}>
      {kids()}
    </span>
  );
  const asIs = () => <Fragment key={key}>{kids()}</Fragment>;
  switch (tag) {
    case 'b':
      return <strong key={key}>{kids()}</strong>;
    case 'i':
      return <em key={key}>{kids()}</em>;
    case 'u':
      return <u key={key}>{kids()}</u>;
    case 's':
      return <s key={key}>{kids()}</s>;
    case 'sub':
      return <sub key={key}>{kids()}</sub>;
    case 'sup':
      return <sup key={key}>{kids()}</sup>;
    case 'small':
      return <small key={key}>{kids()}</small>;
    case 'center':
    case 'left':
    case 'right':
    case 'justify':
      return span({ textAlign: tag }, 'bb-block');
    case 'color': {
      const c = safeColor(arg);
      return c ? span({ color: c }) : asIs();
    }
    case 'highlight': {
      const c = safeColor(arg);
      return c ? span({ backgroundColor: c }, 'bb-highlight') : asIs();
    }
    case 'size': {
      const n = Number(arg);
      return n > 0 ? span({ fontSize: `${Math.min(200, Math.max(50, n))}%` }) : asIs();
    }
    case 'url': {
      const href = safeUrl(arg || body);
      if (!href || noLinks) return <Fragment key={key}>{arg ? kids(true) : body}</Fragment>;
      return (
        <a key={key} href={href} {...external(href)}>
          {arg ? kids(true) : body}
        </a>
      );
    }
    case 'email': {
      const addr = (arg || body).trim();
      if (noLinks || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(addr)) return <Fragment key={key}>{arg ? kids(true) : body}</Fragment>;
      return (
        <a key={key} href={`mailto:${addr}`}>
          {arg ? kids(true) : body}
        </a>
      );
    }
    case 'img': {
      const src = safeUrl(body, true);
      return src ? <img key={key} src={src} alt={arg} loading="lazy" className="md-img" /> : m[0];
    }
    case 'code':
      return <code key={key}>{body}</code>;
    case 'youtube': {
      // a link instead of an embedded player
      const id = /(?:v=|youtu\.be\/|embed\/)?([\w-]{6,})\s*$/.exec(body.trim())?.[1];
      if (!id || noLinks) return body;
      return (
        <a key={key} href={`https://www.youtube.com/watch?v=${id}`} target="_blank" rel="noopener noreferrer">
          ▶ YouTube {id}
        </a>
      );
    }
  }
  if (/^h[1-6]$/.test(tag)) return span({}, `bb-block bb-h bb-${tag}`);
  return m[0];
}

function markdownInline(text: string, keyBase: string, syn: Syntax, noLinks = false): ReactNode[] {
  const out: ReactNode[] = [];
  let last = 0;
  let k = 0;
  const key = () => `${keyBase}.${k++}`;
  for (const m of text.matchAll(INLINE)) {
    const start = m.index!;
    let lead = '';
    let node: ReactNode;
    if (!syn.md && (m[1] || m[4] !== undefined || m[6] !== undefined || m[7] !== undefined || m[9] !== undefined)) {
      node = m[0]; // Markdown is off: keep the characters
    } else if (m[1]) {
      node = <code key={key()}>{m[2]}</code>;
    } else if (m[4] !== undefined) {
      const src = safeUrl(m[4], true);
      node = src ? <img key={key()} src={src} alt={m[3]} loading="lazy" className="md-img" /> : m[0];
    } else if (m[6] !== undefined) {
      const href = safeUrl(m[6]);
      node =
        href && !noLinks ? (
          <a key={key()} href={href} {...external(href)}>
            {inline(m[5], key(), syn, true)}
          </a>
        ) : (
          m[0]
        );
    } else if (m[7] !== undefined) {
      node = <strong key={key()}>{inline(m[7], key(), syn, noLinks)}</strong>;
    } else if (m[9] !== undefined) {
      lead = m[8];
      node = <em key={key()}>{inline(m[9], key(), syn, noLinks)}</em>;
    } else if (m[10]) {
      node = noLinks ? (
        m[10]
      ) : (
        <a key={key()} href={m[10]} target="_blank" rel="noopener noreferrer">
          {m[10]}
        </a>
      );
    } else if (m[12]) {
      lead = m[11];
      node = noLinks ? `#${m[12]}` : (
        <Link key={key()} to={`/issues/${m[12]}`} className="issue-ref">
          #{m[12]}
        </Link>
      );
    } else if (m[14]) {
      lead = m[13];
      node = noLinks ? `~${m[14]}` : <a key={key()} href={`#c${m[14]}`}>~{m[14]}</a>;
    } else if (m[16]) {
      lead = m[15];
      node = (
        <span key={key()} className="mention">
          @{m[16]}
        </span>
      );
    }
    out.push(text.slice(last, start) + lead);
    out.push(node);
    last = start + m[0].length;
  }
  out.push(text.slice(last));
  return out.filter((n) => n !== '');
}

const HR = /^ {0,3}([-*_])( ?\1){2,}\s*$/;
const HEADING = /^(#{1,3})\s+(.+?)\s*#*\s*$/;
const BULLET = /^(\s*)[-*+]\s+(.*)$/;
const ORDERED = /^(\s*)(\d{1,9})[.)]\s+(.*)$/;
const FENCE = /^\s*```/;
const QUOTE = /^\s*>\s?(.*)$/;
// BBCode blocks: [code]…[/code], [list]…[/list], [quote]…[/quote], [table]…[/table] starting a line, and [hr]
const BB_BLOCK = /^\s*\[(code|list|quote|table)(?:=([^\]\n]*))?\]/i;
const BB_HR = /^\s*\[hr\]\s*$/i;

const mdBlock = (line: string) => FENCE.test(line) || HEADING.test(line) || QUOTE.test(line) || BULLET.test(line) || ORDERED.test(line) || HR.test(line);
const bbBlock = (line: string) => BB_BLOCK.test(line) || BB_HR.test(line);
const startsBlock = (line: string, syn: Syntax) => (syn.md && mdBlock(line)) || (syn.bb && bbBlock(line));

/** Which syntax a text uses (for the editor of an "auto" text). */
export function detectFormat(text: string): 'text' | 'markdown' | 'bbcode' | 'mixed' {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const bb = new RegExp(BB_INLINE.source, 'i').test(text) || lines.some(bbBlock);
  const md = lines.some(mdBlock) || [...text.matchAll(INLINE)].some((m) =>
    m[1] || m[4] !== undefined || m[6] !== undefined || m[7] !== undefined || m[9] !== undefined,
  );
  return bb && md ? 'mixed' : bb ? 'bbcode' : md ? 'markdown' : 'text';
}

function bbTable(inner: string, key: string, syn: Syntax): ReactNode {
  const cells = (row: string) => [...row.matchAll(/\[(td|th)\]([\s\S]*?)\[\/\1\]/gi)];
  const rows = [...inner.matchAll(/\[tr\]([\s\S]*?)\[\/tr\]/gi)].map((r) => cells(r[1]));
  const all = rows.length ? rows : [cells(inner)];
  return (
    <div key={key} className="table-scroll">
      <table className="bb-table">
        <tbody>
          {all.map((row, r) => (
            <tr key={r}>
              {row.map((c, i) =>
                c[1].toLowerCase() === 'th' ? <th key={i}>{inline(c[2].trim(), `${key}.${r}.${i}`, syn)}</th> : <td key={i}>{inline(c[2].trim(), `${key}.${r}.${i}`, syn)}</td>,
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function blocks(text: string, keyBase: string, syn: Syntax): ReactNode[] {
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  const out: ReactNode[] = [];
  let i = 0;
  let k = 0;
  const key = () => `${keyBase}-${k++}`;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === '') {
      i++;
      continue;
    }
    if (syn.md && FENCE.test(line)) {
      const code: string[] = [];
      i++;
      while (i < lines.length && !FENCE.test(lines[i])) code.push(lines[i++]);
      i++; // closing fence
      out.push(
        <pre key={key()} className="md-code">
          <code>{code.join('\n')}</code>
        </pre>,
      );
      continue;
    }
    const bb = syn.bb ? BB_BLOCK.exec(line) : null;
    if (bb) {
      // the block runs to its closing tag (or to the end of the text)
      const tag = bb[1].toLowerCase();
      const body = lines.slice(i).join('\n').slice(bb[0].length);
      const close = new RegExp(`\\[/${tag}\\]`, 'i').exec(body);
      const inner = close ? body.slice(0, close.index) : body;
      const after = close ? body.slice(close.index + close[0].length).replace(/^[ \t]*\n/, '') : '';
      if (tag === 'code') {
        out.push(
          <pre key={key()} className="md-code">
            <code>{inner.replace(/^\n/, '').replace(/\n$/, '')}</code>
          </pre>,
        );
      } else if (tag === 'quote') {
        out.push(
          <blockquote key={key()}>
            {blocks(inner, key(), syn)}
            {bb[2] && <div className="bb-cite">— {bb[2].trim()}</div>}
          </blockquote>,
        );
      } else if (tag === 'table') {
        out.push(bbTable(inner, key(), syn));
      } else {
        const items = inner.split(/\[\*\]/).slice(1).map((s) => s.trim());
        const lis = items.map((it, n) => <li key={n}>{inline(it, `${key()}.${n}`, syn)}</li>);
        out.push(
          bb[2] && /^[1aAiI]$/.test(bb[2].trim()) ? (
            <ol key={key()} type={bb[2].trim() as '1' | 'a' | 'A' | 'i' | 'I'}>
              {lis}
            </ol>
          ) : (
            <ul key={key()}>{lis}</ul>
          ),
        );
      }
      // continue with what follows the closing tag
      lines.splice(i, lines.length - i, ...(after ? after.split('\n') : []));
      continue;
    }
    if (syn.bb && BB_HR.test(line)) {
      out.push(<hr key={key()} />);
      i++;
      continue;
    }
    const h = syn.md ? HEADING.exec(line) : null;
    if (h) {
      const Tag = (['h3', 'h4', 'h5'] as const)[h[1].length - 1];
      out.push(<Tag key={key()} className="md-h">{inline(h[2], key(), syn)}</Tag>);
      i++;
      continue;
    }
    if (syn.md && HR.test(line)) {
      out.push(<hr key={key()} />);
      i++;
      continue;
    }
    if (syn.md && QUOTE.test(line)) {
      const inner: string[] = [];
      while (i < lines.length && QUOTE.test(lines[i])) inner.push(QUOTE.exec(lines[i++])![1]);
      out.push(<blockquote key={key()}>{blocks(inner.join('\n'), key(), syn)}</blockquote>);
      continue;
    }
    const bullet = syn.md ? BULLET.exec(line) : null;
    const ordered = syn.md ? ORDERED.exec(line) : null;
    if (bullet || ordered) {
      const isOrdered = !bullet;
      const items: string[] = [];
      while (i < lines.length) {
        const m = isOrdered ? ORDERED.exec(lines[i]) : BULLET.exec(lines[i]);
        if (m) {
          items.push(isOrdered ? m[3] : m[2]);
          i++;
        } else if (lines[i].trim() !== '' && /^\s{2,}/.test(lines[i]) && items.length) {
          // an indented line continues the item
          items[items.length - 1] += '\n' + lines[i++].trim();
        } else break;
      }
      const lis = items.map((it, n) => <li key={n}>{inline(it, `${key()}.${n}`, syn)}</li>);
      out.push(
        isOrdered ? (
          <ol key={key()} start={Number(ordered![2])}>
            {lis}
          </ol>
        ) : (
          <ul key={key()}>{lis}</ul>
        ),
      );
      continue;
    }
    // a paragraph: lines up to a blank line or another block; line breaks and spaces are kept
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() !== '' && (para.length === 0 || !startsBlock(lines[i], syn))) para.push(lines[i++]);
    out.push(
      <p key={key()} className="md-p">
        {inline(para.join('\n'), key(), syn)}
      </p>,
    );
  }
  return out;
}

export function Markdown({ text, format = 'auto' }: { text: string; format?: TextFormat }) {
  const syn = SYNTAX[format] ?? SYNTAX.auto;
  return (
    <>
      {blocks(text, 'b', syn).map((n, i) => (
        <Fragment key={i}>{n}</Fragment>
      ))}
    </>
  );
}
